import React from 'react';
import { Avatar, Modal, Tooltip } from 'flowbite-react';
import { RxCross2 } from 'react-icons/rx';
import { BsLinkedin } from 'react-icons/bs';
import { BsWhatsapp } from 'react-icons/bs';
import { FaXTwitter } from 'react-icons/fa6';
import { PositionTitle, UIPosition } from 'types';
import { generatePositionTitle } from 'shared-utils';
import { APP_ENDPOINT } from 'src/config/env.config';
import { copyToClipboard } from 'src/utils/copyToClipboard.utils';
import { useToast } from 'src/context/ToastProvider';
import { HiBriefcase, HiCurrencyRupee, HiLocationMarker } from 'react-icons/hi';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	positionDetail: UIPosition;
	avatarImg: string;
}

const ShareablePositionModal = ({ isOpen, onClose, positionDetail, avatarImg }: Props) => {
	const toast = useToast();
	const positionTitle = generatePositionTitle(positionDetail.title as PositionTitle);
	const refLink = `$/${positionDetail._id}`;

	const handleLinkedInClick = () => {
		const postData = {
			url: refLink,
		};

		const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postData.url)}`;

		window.open(linkedInShareUrl, '_blank');
	};
	const handleTwitterClick = () => {
		const postData = {
			url: refLink,
			text: `${positionDetail.company.name} is hiring for ${positionTitle} with ${positionDetail.positionRequirement.workExperience.min} - ${positionDetail.positionRequirement.workExperience.max} years of experience.
            
Prove yourself in a technical round with Industry Experts to get shortlisted`,
		};

		const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
			postData.text,
		)}&url=${encodeURIComponent(postData.url)}`;

		window.open(twitterShareUrl, '_blank');
	};

	const handleWhatsAppClick = () => {
		const message = `${positionDetail.company.name} is hiring for ${positionTitle} with ${positionDetail.positionRequirement.workExperience.min} - ${positionDetail.positionRequirement.workExperience.max} years of experience.
Prove yourself in a technical round with Industry Experts to get shortlisted ${refLink}`;
		const whatsappLink = `https://wa.me/?text=${encodeURIComponent(message)}`;

		window.open(whatsappLink, '_blank');
	};

	const handleCopy = () => {
		copyToClipboard(refLink);
		toast({
			type: 'success',
			title: 'Copied to clipboard',
		});
	};
	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size={'xl'}>
			<Modal.Body className="bg-white dark:bg-[#1F2A37] px-0 rounded-[6px] pb-0">
				<div>
					<div className="flex flex-col gap-2 px-3 pb-4">
						<div className="flex gap-[6px] md:gap-3 w-full">
							<div className="basis-[14%] flex items-center justify-center">
								<Avatar img={avatarImg} size={36} rounded bordered />
							</div>
							<div className=" flex flex-col gap-[2px] basis-[60%]">
								<p className="font-[700] text-[26px] leading-[24px] py-[5px] md:max-w-full max-w-[240px] sm:text-lg">
									{positionTitle.length > 20 ? (
										<Tooltip content={positionTitle} placement="top">
											{positionTitle.substring(0, 17)}...
										</Tooltip>
									) : (
										<>{positionTitle}</>
									)}
								</p>
								<p className="text-[#7777E7] dark:text-[#C7C9D9] text-[16px] truncate md:max-w-full max-w-[240px]">
									{positionDetail.company.name}
								</p>
							</div>
						</div>
						<div className="flex md:mb-2 sm:gap-x-4 mt-1 sm:gap-y-[6px] md:pl-4 flex-col  md:gap-[6px]">
							{/* Working Type / Experience */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiBriefcase className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
									<div>{positionDetail.workingMode}</div>
									<div className="font-inter font-[500] text-[24px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
										•
									</div>
									<div>
										{`${Math.floor(positionDetail.positionRequirement.workExperience.min)} Yrs ${
											Math.ceil(positionDetail.positionRequirement.workExperience?.min * 12) % 12
										} Mon - ${Math.floor(positionDetail.positionRequirement.workExperience.max)} Yrs ${
											Math.ceil(positionDetail.positionRequirement.workExperience?.max * 12) % 12
										} Mon `}
									</div>
								</div>
							</div>
							{/* Salary Package */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiCurrencyRupee className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
									{positionDetail.salary.min} - {positionDetail.salary.max} LPA
								</div>
							</div>
							{/* Job Location / Working Mode */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiLocationMarker className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#28293D] dark:text-[#E4E4EB]">
									<div className="pr-1">{positionDetail.workLocation}</div>
									<div className="font-inter font-[500] text-[24px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
										•
									</div>
									<div>{positionDetail.positionType}</div>
								</div>
							</div>
						</div>
					</div>
					<div
						style={{
							backgroundImage: 'url("/assets/shareablePositionBg.png")',
						}}
						className="px-3 flex flex-col gap-5 shareable-position-bg-div bg-no-repeat bg-cover pb-6 pt-2"
					>
						<div className="max-w-[353px] mx-auto">
							<p className="leading-[28px] font-[500] text-[#F2F2F5] py-2 text-center text-[20px]">
								Share this job with your network{' '}
							</p>
						</div>
						<div className="flex items-center justify-center gap-4">
							<Avatar
								size={'md'}
								img={BsLinkedin}
								className="text-white cursor-pointer"
								rounded
								bordered
								onClick={handleLinkedInClick}
							/>
							<Avatar
								size={'md'}
								img={FaXTwitter}
								className="cursor-pointer text-white"
								rounded
								bordered
								onClick={handleTwitterClick}
							/>
							<Avatar
								size={'md'}
								img={BsWhatsapp}
								className="text-white cursor-pointer"
								rounded
								bordered
								onClick={handleWhatsAppClick}
							/>
						</div>
						<div className="bg-[#374151] mx-auto md:max-w-[90%] py-2 px-4 rounded-[8px] flex justify-between items-center">
							<div className="truncate sm:max-w-[220px] md:max-w-[75%] text-[16px] text-white">{refLink}</div>
							<div className="text-[13px] md:text-[16px] text-[#6698FA] cursor-pointer" onClick={handleCopy}>
								Copy link
							</div>
						</div>
					</div>
				</div>
				<RxCross2
					tabIndex={0}
					onClick={onClose}
					className="absolute right-[16px] top-[16px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]"
				/>
			</Modal.Body>
		</Modal>
	);
};

export default ShareablePositionModal;
